import { useEffect, useState } from "react";
import { Sparkles, ShieldCheck, CircleHelp, AlertTriangle, Loader2 } from "lucide-react";
import { SourceBadge } from "@/components/SourceBadge";
import { Button } from "@/components/ui/button";
import {
  getIntelStatus,
  fetchScoreExplanation,
  fetchDomainInsight,
  fetchOpportunityAnalysis,
} from "@/lib/intelligence";

const CONFIDENCE = {
  high: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/25",
  medium: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/25",
  low: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/25",
};
const VERIFICATION = {
  VERIFIED: { label: "Verified", cls: "text-teal-700 dark:text-teal-300 border-teal-500/30 bg-teal-500/10", Icon: ShieldCheck },
  INFERRED: { label: "Inferred", cls: "text-slate-700 dark:text-slate-300 border-slate-500/30 bg-slate-500/10", Icon: CircleHelp },
  REQUIRES_VERIFICATION: { label: "Requires verification", cls: "text-amber-700 dark:text-amber-300 border-amber-500/30 bg-amber-500/10", Icon: AlertTriangle },
  INSUFFICIENT_EVIDENCE: { label: "Insufficient evidence", cls: "text-red-700 dark:text-red-300 border-red-500/30 bg-red-500/10", Icon: AlertTriangle },
};

function Chip({ className, children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${className}`}>
      {children}
    </span>
  );
}

function Meta({ insight }) {
  const conf = insight.confidence || "low";
  const ver = VERIFICATION[insight.verification_status] || VERIFICATION.INFERRED;
  const VIcon = ver.Icon;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="insight-meta">
      <SourceBadge type="ai" />
      <Chip className={CONFIDENCE[conf]} data-testid="insight-confidence">Confidence: {conf}</Chip>
      <Chip className={ver.cls}><VIcon className="h-3 w-3" />{ver.label}</Chip>
    </div>
  );
}

function List({ label, items }) {
  if (!items || !items.length) return null;
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <ul className="space-y-1.5">
        {items.map((t, i) => (
          <li key={`${label}-${i}`} className="flex gap-2 text-sm text-muted-foreground">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InsightBody({ insight, variant }) {
  if (variant === "opportunity") {
    return (
      <div className="space-y-4" data-testid="insight-body">
        {insight.business_problem && <p className="text-sm leading-relaxed">{insight.business_problem}</p>}
        <List label="Why ORBIT identified this" items={insight.why_identified} />
        <List label="Supporting evidence" items={insight.supporting_evidence} />
        <List label="Potential outcomes" items={insight.potential_outcomes} />
        <List label="What is required first" items={insight.what_is_required} />
        {insight.first_step && (
          <div className="rounded-xl border border-accent/30 bg-accent/5 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-accent">First step</p>
            <p className="mt-1 text-sm">{insight.first_step}</p>
          </div>
        )}
        <List label="Not yet" items={insight.not_yet} />
        {insight.confidence_reason && <p className="text-xs text-muted-foreground">Why this confidence: {insight.confidence_reason}</p>}
        <List label="Verify before acting" items={insight.verification_items} />
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="insight-body">
      {insight.summary && <p className="text-sm leading-relaxed">{insight.summary}</p>}
      {insight.reasoning && <p className="text-sm leading-relaxed text-muted-foreground">{insight.reasoning}</p>}
      <List label="Evidence used" items={insight.evidence} />
      {insight.recommended_action && (
        <div className="rounded-xl border border-accent/30 bg-accent/5 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-accent">Recommended next action</p>
          <p className="mt-1 text-sm">{insight.recommended_action}</p>
        </div>
      )}
      {insight.confidence_reason && <p className="text-xs text-muted-foreground">Why this confidence: {insight.confidence_reason}</p>}
      <List label="Requires verification" items={insight.verification_items} />
    </div>
  );
}

// Reusable, lazy-loaded ORBIT Intelligence panel. Deterministic content is always
// shown by the parent page; this only adds AI *interpretation* on demand and
// degrades gracefully when AI is disabled or unavailable.
export function InsightPanel({ operation, payload, heading = "ORBIT Insight", variant = "insight", testid }) {
  const [enabled, setEnabled] = useState(null); // null = unknown
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    getIntelStatus().then((s) => alive && setEnabled(!!s.enabled));
    return () => {
      alive = false;
    };
  }, []);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (operation === "score") data = await fetchScoreExplanation(payload.score_key);
      else if (operation === "domain") data = await fetchDomainInsight(payload.domain_key);
      else data = await fetchOpportunityAnalysis(payload.opportunity_id);
      setResult(data);
    } catch (e) {
      setError("AI insight currently unavailable. ORBIT's calculated assessment remains available.");
    } finally {
      setLoading(false);
    }
  };

  const tid = testid || `insight-panel-${operation}`;

  if (enabled === false) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-4" data-testid={`${tid}-disabled`}>
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" /> ORBIT AI insights are turned off. Calculated results remain fully available.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid={tid}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-accent" />
          <h3 className="font-display text-base font-semibold">{heading}</h3>
        </div>
        {!result && (
          <Button size="sm" variant="outline" onClick={run} disabled={loading || enabled === null} data-testid={`${tid}-btn`}>
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</> : <><Sparkles className="h-4 w-4" /> Explain with ORBIT Intelligence</>}
          </Button>
        )}
      </div>

      {error && !result && (
        <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-700 dark:text-amber-300" data-testid={`${tid}-error`}>{error}</p>
      )}

      {result && result.source === "unavailable" && (
        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid={`${tid}-unavailable`}>
          <p className="text-sm text-amber-700 dark:text-amber-300">{result.message}</p>
        </div>
      )}

      {result && result.source === "ai" && (
        <div className="mt-4 space-y-4" data-testid={`${tid}-result`}>
          <Meta insight={result} />
          {result.title && <p className="font-display text-sm font-semibold">{result.title}</p>}
          <InsightBody insight={result} variant={variant} />
          {result.model && (
            <p className="pt-1 text-[11px] text-muted-foreground">Generated by ORBIT Intelligence · {result.model} · grounded in your assessment evidence</p>
          )}
        </div>
      )}
    </div>
  );
}
